<template>
  <div class="site-menu-history-meta-info">
    <!-- PDF URI -->
    <span v-if="pdfUri" class="site-menu-history-meta-info__item">
      <icon name="pubcom" color="gray-300" size="md" class="site-menu-history-meta-info__item__icon" />
      <span class="site-menu-history-meta-info__item__text">{{ pdfUri.split('//').slice(1).join('/') }}</span>
    </span>

    <!-- 法令 -->
    <template v-if="isLaw">
      <div v-if="(document.lawOfficialTitle?.length ?? 0) > 0" class="site-menu-history-meta-info__item">
        <span class="site-menu-history-meta-info__item__text">{{ document.lawOfficialTitle }}</span>
      </div>
      <template v-if="document.publishedOn && document.publishedOn.length > 0">
        <div class="site-menu-history-meta-info__item">
          <icon name="calendar-edit" color="gray-300" size="md" class="site-menu-history-meta-info__item__icon" />
          <span class="site-menu-history-meta-info__item__text">{{ formatYmd(document.publishedOn) }} 施行</span>
        </div>
      </template>
    </template>

    <!-- 著者、出版社、出版日 -->
    <template v-else>
      <!-- 著者 -->
      <span v-if="authors && authors.length > 0" class="site-menu-history-meta-info__item">
        <icon name="account-tie" color="gray-300" size="md" class="site-menu-history-meta-info__item__icon" />
        <span class="site-menu-history-meta-info__item__text">
          <span v-for="author in authors" :key="author" class="site-menu-history-meta-info__item__text__item">
            <rendered-span :html-string="author" />
          </span>
        </span>
      </span>

      <!-- 出版社 -->
      <span v-if="publisher" class="site-menu-history-meta-info__item">
        <icon name="office-building" color="gray-300" size="md" class="site-menu-history-meta-info__item__icon" />
        <rendered-span class="site-menu-history-meta-info__item__text" :html-string="publisher" />
      </span>

      <!-- 出版日 -->
      <span v-if="showPublishedOn" class="site-menu-history-meta-info__item">
        <icon name="calendar-edit" color="gray-300" size="md" class="site-menu-history-meta-info__item__icon" />
        <span class="site-menu-history-meta-info__item__text">{{ formatYmd(publishedOn) }}</span>
      </span>
    </template>

    <!-- 委員会報告書 -->
    <template v-if="comittieeReport.stockExchange || comittieeReport.industry || comittieeReport.quality">
      <span v-if="comittieeReport.stockExchange" class="site-menu-history-meta-info__item">
        <icon name="finance" color="gray-300" size="md" class="site-menu-history-meta-info__item__icon" />
        <span class="site-menu-history-meta-info__item__text">{{ comittieeReport.stockExchange }}</span>
      </span>

      <span v-if="comittieeReport.industry" class="site-menu-history-meta-info__item">
        <icon name="domain" color="gray-300" size="md" class="site-menu-history-meta-info__item__icon" />
        <span class="site-menu-history-meta-info__item__text">{{ comittieeReport.industry }}</span>
      </span>

      <v-tooltip v-if="comittieeReport.quality" bottom class="site-menu-history-meta-info__tooltip">
        <template #activator="{ on }">
          <span class="site-menu-history-meta-info__item" v-on="on">
            <icon name="diamond-stone" color="gray-300" size="md" class="site-menu-history-meta-info__item__icon" />
            <span>データ品質</span>
            <icon
              v-if="0 < comittieeReport.quality && comittieeReport.quality < 4"
              :name="qualityIconName(comittieeReport.quality)"
              color="gray-300"
              size="2md"
              class="site-menu-history-meta-info__item__icon"
            />
          </span>
        </template>
        <ol class="site-menu-history-meta-info__desc-list">
          <li class="site-menu-history-meta-info__desc-list__item">
            <icon name="alpha-a-circle" color="gray-300" size="md" />
            <span>テキスト抽出に概ね～全く問題がありません</span>
          </li>
          <li class="site-menu-history-meta-info__desc-list__item">
            <icon name="alpha-b-circle" color="gray-300" size="md" />
            <span>OCR処理によりある程度のテキストが抽出されています</span>
          </li>
          <li class="site-menu-history-meta-info__desc-list__item">
            <icon name="alpha-c-circle" color="gray-300" size="md" />
            <span>OCR処理により一部のテキストが抽出されています</span>
          </li>
        </ol>
        <p class="site-menu-history-meta-info__tooltip__caption">
          ※ 収録の際テキストが完全に抽出できていない文書は、<br />
          検索結果から漏れる可能性があるため、注意が必要です。
        </p>
      </v-tooltip>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { formatYmd } from '@/utility';
import { DocRecord, DocumentTypeEnum } from 'wklr-backend-sdk/models';

import Icon, { type IconProps } from '@/components/renewal/common/icon.vue';

export interface SiteMenuHistoryMetaInfoProps {
  document: DocRecord;
  docRecordType?: DocumentTypeEnum;
  pdfUri?: string;
  authors?: string[];
  publisher?: string;
  showPublishedOn: boolean;
  publishedOn?: Date | null;
  comittieeReport: {
    stockExchange?: string;
    industry?: string;
    quality?: number;
  };
}

const props = defineProps<SiteMenuHistoryMetaInfoProps>();

const isLaw = computed(() => props.docRecordType === DocumentTypeEnum.Law);

const qualityIconName = (quality: number): IconProps['name'] => {
  // v-if で 0 < quality && quality < 4 としているため、これは起こりえないはず
  if (quality < 1 || 3 < quality) throw new Error('Invalid quality');

  const q = quality === 1 ? 'c' : quality === 2 ? 'b' : 'a';
  return `alpha-${q}-circle`;
};
</script>

<style lang="scss" scoped src="./site-menu-history-meta-info.scss" />
